import React, { useState, useMemo } from 'react';
import { trpc } from '@/lib/trpc';
import { useAuth } from '@/_core/hooks/useAuth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import type { inferRouterOutputs } from '@trpc/server';
import type { AppRouter } from '../../../server/routers';
import {
  Dumbbell, Flame, Trophy, TrendingUp, Target, Scale, Zap,
  BarChart3, User, Pencil, Check, X, Heart, MapPin, Timer, Activity, Calendar, Clock, Plus, Trash2
} from 'lucide-react';
import { subMonths, format } from 'date-fns';

// ─── Types ────────────────────────────────────────────────────────────────────
type RouterOutput = inferRouterOutputs<AppRouter>;
type Stats = RouterOutput['profile']['stats'];
type ChartType =
  | 'volume' | 'frequency' | 'weight' | 'cardio' | 'heatmap' | 'muscles'
  | 'prs' | 'sets' | 'streak' | 'distance' | 'pace' | 'hr' | null;

type TimeRange = '1m' | '3m' | 'all';

// ─── Helpers ──────────────────────────────────────────────────────────────────
function fmtPace(minPerMile: number) {
  const m = Math.floor(minPerMile);
  const s = Math.round((minPerMile - m) * 60);
  return `${m}:${String(s).padStart(2, '0')}/mi`;
}

function weekLabel(weekKey: string) {
  // YYYY-Www → "Wnn"
  return weekKey.split('-')[1] ?? weekKey;
}

/** Returns a cutoff date string (YYYY-MM-DD) for the given range, or null for all-time. */
function getCutoff(range: TimeRange): string | null {
  if (range === 'all') return null;
  const months = range === '1m' ? 1 : 3;
  return format(subMonths(new Date(), months), 'yyyy-MM-dd');
}

// ─── Mini SVG line chart ───────────────────────────────────────────────────────
function LineChart({ data, color = '#22c55e', labels }: {
  data: number[];
  color?: string;
  labels?: string[];
}) {
  const [hovered, setHovered] = useState<number | null>(null);
  if (data.length < 2) return null;
  const max = Math.max(...data, 1);
  const W = 280, H = 80;
  const toX = (i: number) => (i / (data.length - 1)) * W;
  const toY = (v: number) => H - (v / max) * H * 0.85 - 4;
  const pts = data.map((v, i) => `${toX(i)},${toY(v)}`).join(' ');
  return (
    <div className="relative">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="w-full overflow-visible"
        style={{ height: 80 }}
        onMouseLeave={() => setHovered(null)}
      >
        <polyline
          points={pts}
          fill="none"
          stroke={color}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {data.map((v, i) => {
          const cx = toX(i);
          const cy = toY(v);
          return v > 0 ? (
            <g key={i}>
              <circle cx={cx} cy={cy} r={hovered === i ? 4 : 2.5} fill={color} style={{ transition: 'r 0.1s' }} />
              <circle cx={cx} cy={cy} r={8} fill="transparent" onMouseEnter={() => setHovered(i)} />
            </g>
          ) : null;
        })}
      </svg>
      {hovered !== null && data[hovered] > 0 && (
        <div
          className="absolute z-20 bg-zinc-800 border border-zinc-600 rounded px-2 py-1 pointer-events-none"
          style={{
            left: `${(toX(hovered) / W) * 100}%`,
            top: 0,
            transform: 'translateX(-50%) translateY(-110%)',
          }}
        >
          <p className="font-mono text-[10px] text-white">{data[hovered]}</p>
          {labels?.[hovered] && <p className="font-mono text-[8px] text-muted-foreground">{labels[hovered]}</p>}
        </div>
      )}
    </div>
  );
}

// ─── Bar chart ────────────────────────────────────────────────────────────────
function BarChart({ data, color = '#22c55e', formatVal, pbValue }: {
  data: { label: string; value: number }[];
  color?: string;
  formatVal?: (v: number) => string;
  pbValue?: number;
}) {
  const [hovered, setHovered] = useState<number | null>(null);
  const max = Math.max(...data.map((d) => d.value), pbValue ?? 0, 1);
  const containerH = 96;
  const barAreaH = 80;
  return (
    <div className="relative">
      {/* PB annotation line */}
      {pbValue !== undefined && pbValue > 0 && (
        <div
          className="absolute left-0 right-0 border-t border-dashed border-yellow-400/70 z-10 pointer-events-none"
          style={{ bottom: `${(pbValue / max) * barAreaH + 16}px` }}
        >
          <span className="absolute right-0 -top-3 font-mono text-[8px] text-yellow-400 bg-background px-1">PB</span>
        </div>
      )}
      <div className="flex items-end gap-1 w-full" style={{ height: containerH }}>
        {data.map((d, i) => {
          const barH = Math.max((d.value / max) * barAreaH, d.value > 0 ? 4 : 0);
          const isCurrent = i === data.length - 1;
          const displayVal = formatVal ? formatVal(d.value) : String(d.value);
          return (
            <div
              key={i}
              className="relative flex flex-col items-center gap-0.5 flex-1 min-w-0 cursor-pointer"
              onMouseEnter={() => setHovered(i)}
              onMouseLeave={() => setHovered(null)}
            >
              {/* Tooltip */}
              {hovered === i && d.value > 0 && (
                <div className="absolute bottom-full mb-1 z-20 bg-zinc-800 border border-zinc-600 rounded px-2 py-1 whitespace-nowrap pointer-events-none">
                  <p className="font-mono text-[10px] text-white">{displayVal}</p>
                  <p className="font-mono text-[8px] text-muted-foreground">{d.label}</p>
                </div>
              )}
              <div
                className="w-full rounded-sm transition-all duration-300"
                style={{
                  height: `${barH}px`,
                  backgroundColor: isCurrent ? color : `${color}80`,
                }}
              />
              <span className="font-mono text-[7px] text-muted-foreground truncate w-full text-center">
                {d.label.replace(/^\d{4}-/, '')}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Heatmap ──────────────────────────────────────────────────────────────────
function heatmapColor(count: number, maxCount: number): string {
  if (count <= 0) return '#1f2937'; // dark empty
  const intensity = Math.min(count / Math.max(maxCount, 1), 1);
  // gradient: low → #166534 (dark green), high → #4ade80 (bright green)
  const r = Math.round(22 + intensity * (74 - 22));
  const g = Math.round(101 + intensity * (222 - 101));
  const b = Math.round(52 + intensity * (128 - 52));
  return `rgb(${r},${g},${b})`;
}

function HeatmapGrid({ days }: { days: { date: string; count: number }[] }) {
  const [tooltip, setTooltip] = useState<{ date: string; count: number; x: number; y: number } | null>(null);
  const maxCount = Math.max(...days.map((d) => d.count), 1);
  // 84 days = 12 cols × 7 rows
  const cols: { date: string; count: number }[][] = [];
  for (let i = 0; i < days.length; i += 7) {
    cols.push(days.slice(i, i + 7));
  }
  return (
    <div className="relative">
      {tooltip && (
        <div
          className="fixed z-50 bg-zinc-800 border border-zinc-600 rounded px-2 py-1 pointer-events-none"
          style={{ left: tooltip.x + 8, top: tooltip.y - 32 }}
        >
          <p className="font-mono text-[10px] text-white">{tooltip.count} session{tooltip.count !== 1 ? 's' : ''}</p>
          <p className="font-mono text-[8px] text-muted-foreground">{tooltip.date}</p>
        </div>
      )}
      <div className="flex gap-1">
        {cols.map((col, ci) => (
          <div key={ci} className="flex flex-col gap-1">
            {col.map((day) => (
              <div
                key={day.date}
                className="w-4 h-4 rounded-sm cursor-pointer transition-transform hover:scale-125"
                style={{ backgroundColor: heatmapColor(day.count, maxCount) }}
                onMouseEnter={(e) => setTooltip({ date: day.date, count: day.count, x: e.clientX, y: e.clientY })}
                onMouseMove={(e) => setTooltip((t) => t ? { ...t, x: e.clientX, y: e.clientY } : null)}
                onMouseLeave={() => setTooltip(null)}
              />
            ))}
          </div>
        ))}
      </div>
      {/* Gradient legend */}
      <div className="flex items-center gap-2 mt-2">
        <span className="font-mono text-[8px] text-muted-foreground">Less</span>
        <div className="flex gap-0.5">
          {[0, 0.25, 0.5, 0.75, 1].map((v) => (
            <div key={v} className="w-3 h-3 rounded-sm" style={{ backgroundColor: heatmapColor(v * maxCount, maxCount) }} />
          ))}
        </div>
        <span className="font-mono text-[8px] text-muted-foreground">More</span>
      </div>
    </div>
  );
}

// ─── Weight scatter/line chart ────────────────────────────────────────────────
function WeightLineChart({ data }: { data: { date: string; weight: number }[] }) {
  const [hovered, setHovered] = useState<number | null>(null);
  if (data.length < 2) return (
    <p className="font-mono text-xs text-muted-foreground text-center py-4">Not enough data yet</p>
  );
  const weights = data.map((d) => d.weight);
  const pbWeight = Math.max(...weights); // highest weight = all-time peak (PB marker shows where you started)
  const minW = Math.min(...weights) - 2;
  const maxW = Math.max(...weights) + 2;
  const W = 280, H = 100;
  const toX = (i: number) => (i / (data.length - 1)) * W;
  const toY = (w: number) => H - ((w - minW) / (maxW - minW)) * H * 0.85 - 4;
  const pts = data.map((d, i) => `${toX(i)},${toY(d.weight)}`).join(' ');
  const pbY = toY(pbWeight);
  return (
    <div className="space-y-2 relative">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="w-full overflow-visible"
        style={{ height: 100 }}
        onMouseLeave={() => setHovered(null)}
      >
        {/* PB annotation line */}
        <line x1={0} y1={pbY} x2={W} y2={pbY} stroke="#facc15" strokeWidth="1" strokeDasharray="4 3" opacity={0.7} />
        <text x={W - 2} y={pbY - 3} textAnchor="end" fill="#facc15" fontSize="7" fontFamily="monospace">PB</text>
        <polyline
          points={pts}
          fill="none"
          stroke="#22c55e"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {data.map((d, i) => {
          const cx = toX(i);
          const cy = toY(d.weight);
          return (
            <g key={i}>
              <circle cx={cx} cy={cy} r={hovered === i ? 4 : 2.5} fill="#22c55e" style={{ transition: 'r 0.1s' }} />
              {/* Invisible wider hit area */}
              <circle
                cx={cx} cy={cy} r={8}
                fill="transparent"
                onMouseEnter={() => setHovered(i)}
              />
            </g>
          );
        })}
      </svg>
      {/* Tooltip */}
      {hovered !== null && (
        <div
          className="absolute z-20 bg-zinc-800 border border-zinc-600 rounded px-2 py-1 pointer-events-none"
          style={{
            left: `${(toX(hovered) / W) * 100}%`,
            top: 0,
            transform: 'translateX(-50%) translateY(-110%)',
          }}
        >
          <p className="font-mono text-[10px] text-white">{data[hovered].weight} lbs</p>
          <p className="font-mono text-[8px] text-muted-foreground">{data[hovered].date}</p>
        </div>
      )}
      <div className="flex justify-between font-mono text-[9px] text-muted-foreground">
        <span>{data[0].date}</span>
        <span>{data[data.length - 1].date}</span>
      </div>
    </div>
  );
}

// ─── Muscle breakdown bars ────────────────────────────────────────────────────
function MuscleBar({ group, count, total }: { group: string; count: number; total: number }) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div className="space-y-1">
      <div className="flex justify-between font-mono text-[10px]">
        <span className="uppercase tracking-wider text-foreground">{group}</span>
        <span className="text-muted-foreground">{pct}% · {count} sets</span>
      </div>
      <div className="h-1.5 bg-muted rounded-full overflow-hidden">
        <div className="h-full bg-primary rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

// ─── Stat card ────────────────────────────────────────────────────────────────
function StatCard({
  icon: Icon, label, value, sub, accent, onClick,
}: {
  icon: React.ElementType;
  label: string;
  value: string | number;
  sub?: string;
  accent?: boolean;
  onClick?: () => void;
}) {
  return (
    <button
      onClick={onClick}
      disabled={!onClick}
      className={[
        'text-left w-full p-3 rounded-sm border transition-all duration-200',
        accent
          ? 'bg-primary/10 border-primary/30 hover:bg-primary/15'
          : 'bg-card border-border/60 hover:border-primary/40 hover:bg-card/80',
        onClick ? 'cursor-pointer active:scale-[0.98]' : 'cursor-default',
      ].join(' ')}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-mono text-[9px] uppercase tracking-wider text-muted-foreground">{label}</p>
          <p className={`font-mono text-lg font-bold leading-tight mt-0.5 ${accent ? 'text-primary' : 'text-foreground'}`}>
            {value}
          </p>
          {sub && <p className="font-mono text-[9px] text-muted-foreground mt-0.5">{sub}</p>}
        </div>
        <Icon className={`h-4 w-4 shrink-0 mt-0.5 ${accent ? 'text-primary' : 'text-muted-foreground'}`} />
      </div>
      {onClick && (
        <p className="font-mono text-[8px] text-primary/60 mt-1.5 uppercase tracking-wider">tap to view chart →</p>
      )}
    </button>
  );
}

// ─── Time Range Toggle ────────────────────────────────────────────────────────
function TimeRangeToggle({ value, onChange }: { value: TimeRange; onChange: (v: TimeRange) => void }) {
  const options: { label: string; value: TimeRange }[] = [
    { label: '1M', value: '1m' },
    { label: '3M', value: '3m' },
    { label: 'ALL', value: 'all' },
  ];
  return (
    <div className="flex items-center gap-0 border border-border/60 rounded-sm overflow-hidden w-fit">
      {options.map((opt) => (
        <button
          key={opt.value}
          onClick={() => onChange(opt.value)}
          className={[
            'px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider transition-all duration-150',
            value === opt.value
              ? 'bg-primary text-primary-foreground'
              : 'bg-card text-muted-foreground hover:bg-muted hover:text-foreground',
            'border-r border-border/60 last:border-r-0',
          ].join(' ')}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// ─── Drill-down dialog ────────────────────────────────────────────────────────
function DrillDownDialog({ type, stats, filteredStats, onClose }: {
  type: ChartType;
  stats: Stats;
  filteredStats: FilteredStats;
  onClose: () => void;
}) {
  if (!type) return null;

  const titles: Record<NonNullable<ChartType>, string> = {
    volume: 'Weekly Volume Trend',
    frequency: 'Workout Frequency',
    weight: 'Bodyweight Trend',
    cardio: 'Weekly Cardio Distance',
    heatmap: 'Workout Heatmap',
    muscles: 'Muscle Group Breakdown',
    prs: 'Personal Records',
    sets: 'Total Sets',
    streak: 'Current Streak',
    distance: 'Total Distance',
    pace: 'Average Pace',
    hr: 'Average Heart Rate',
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-card border-border/60 max-w-sm w-full">
        <DialogHeader>
          <DialogTitle className="font-mono text-xs uppercase tracking-wider text-primary">
            {titles[type]}
          </DialogTitle>
        </DialogHeader>

        <div className="pt-2 space-y-4">
          {type === 'volume' && (
            <>
              <BarChart
                data={filteredStats.volumeTrend.map((w) => ({ label: weekLabel(w.week), value: w.volumeLbs }))}
                color="#22c55e"
                formatVal={(v) => `${(v / 1000).toFixed(1)}K lbs`}
                pbValue={filteredStats.volumeTrend.length > 0 ? Math.max(...filteredStats.volumeTrend.map((w) => w.volumeLbs)) : 0}
              />
              <div className="flex justify-between font-mono text-[9px] text-muted-foreground">
                <span>{filteredStats.rangeLabel} ago</span>
                <span className="text-primary">This week</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-muted/30 rounded-sm p-2 text-center">
                  <p className="font-mono text-[9px] text-muted-foreground uppercase">Total</p>
                  <p className="font-mono text-sm font-bold text-foreground">
                    {filteredStats.totalVolumeLbs >= 1_000_000
                      ? `${(filteredStats.totalVolumeLbs / 1_000_000).toFixed(1)}M`
                      : `${(filteredStats.totalVolumeLbs / 1_000).toFixed(1)}K`} lbs
                  </p>
                </div>
                <div className="bg-muted/30 rounded-sm p-2 text-center">
                  <p className="font-mono text-[9px] text-muted-foreground uppercase">Peak Week</p>
                  <p className="font-mono text-sm font-bold text-foreground">
                    {filteredStats.volumeTrend.length > 0
                      ? Math.max(...filteredStats.volumeTrend.map((w) => w.volumeLbs)).toLocaleString()
                      : 0} lbs
                  </p>
                </div>
              </div>
            </>
          )}

          {type === 'frequency' && (
            <>
              <BarChart
                data={filteredStats.last12Weeks.map((w) => ({ label: weekLabel(w.week), value: w.count }))}
                color="#22c55e"
              />
              <div className="flex justify-between font-mono text-[9px] text-muted-foreground">
                <span>{filteredStats.rangeLabel} ago</span>
                <span className="text-primary">This week</span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="bg-muted/30 rounded-sm p-2 text-center">
                  <p className="font-mono text-[8px] text-muted-foreground uppercase">Total</p>
                  <p className="font-mono text-sm font-bold text-foreground">{filteredStats.totalSessions}</p>
                </div>
                <div className="bg-muted/30 rounded-sm p-2 text-center">
                  <p className="font-mono text-[8px] text-muted-foreground uppercase">Avg/Wk</p>
                  <p className="font-mono text-sm font-bold text-foreground">
                    {filteredStats.last12Weeks.length > 0
                      ? (filteredStats.last12Weeks.reduce((a, w) => a + w.count, 0) / filteredStats.last12Weeks.length).toFixed(1)
                      : '0.0'}
                  </p>
                </div>
                <div className="bg-muted/30 rounded-sm p-2 text-center">
                  <p className="font-mono text-[8px] text-muted-foreground uppercase">Best Wk</p>
                  <p className="font-mono text-sm font-bold text-foreground">
                    {filteredStats.last12Weeks.length > 0
                      ? Math.max(...filteredStats.last12Weeks.map((w) => w.count))
                      : 0}
                  </p>
                </div>
              </div>
            </>
          )}

          {type === 'weight' && (
            <>
              <WeightLineChart data={filteredStats.weightTrend} />
              {filteredStats.weightTrend.length >= 2 && (
                <div className="grid grid-cols-3 gap-2">
                  <div className="bg-muted/30 rounded-sm p-2 text-center">
                    <p className="font-mono text-[8px] text-muted-foreground uppercase">Start</p>
                    <p className="font-mono text-sm font-bold text-foreground">{filteredStats.weightTrend[0].weight} lbs</p>
                  </div>
                  <div className="bg-muted/30 rounded-sm p-2 text-center">
                    <p className="font-mono text-[8px] text-muted-foreground uppercase">Current</p>
                    <p className="font-mono text-sm font-bold text-primary">
                      {filteredStats.weightTrend[filteredStats.weightTrend.length - 1].weight} lbs
                    </p>
                  </div>
                  <div className="bg-muted/30 rounded-sm p-2 text-center">
                    <p className="font-mono text-[8px] text-muted-foreground uppercase">Change</p>
                    <p className={`font-mono text-sm font-bold ${
                      filteredStats.weightTrend[filteredStats.weightTrend.length - 1].weight - filteredStats.weightTrend[0].weight <= 0
                        ? 'text-green-400' : 'text-orange-400'
                    }`}>
                      {(filteredStats.weightTrend[filteredStats.weightTrend.length - 1].weight - filteredStats.weightTrend[0].weight > 0 ? '+' : '')}
                      {(filteredStats.weightTrend[filteredStats.weightTrend.length - 1].weight - filteredStats.weightTrend[0].weight).toFixed(1)} lbs
                    </p>
                  </div>
                </div>
              )}
            </>
          )}

          {type === 'cardio' && (
            <>
              <BarChart
                data={filteredStats.cardioTrend.map((w) => ({ label: weekLabel(w.week), value: w.distanceMiles }))}
                color="#3b82f6"
                formatVal={(v) => `${v.toFixed(1)} mi`}
                pbValue={filteredStats.cardioTrend.length > 0 ? Math.max(...filteredStats.cardioTrend.map((w) => w.distanceMiles)) : 0}
              />
              <div className="flex justify-between font-mono text-[9px] text-muted-foreground">
                <span>{filteredStats.rangeLabel} ago</span>
                <span className="text-blue-400">This week</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-muted/30 rounded-sm p-2 text-center">
                  <p className="font-mono text-[9px] text-muted-foreground uppercase">Total Distance</p>
                  <p className="font-mono text-sm font-bold text-foreground">{filteredStats.totalDistanceMiles} mi</p>
                </div>
                <div className="bg-muted/30 rounded-sm p-2 text-center">
                  <p className="font-mono text-[9px] text-muted-foreground uppercase">Sessions</p>
                  <p className="font-mono text-sm font-bold text-foreground">{filteredStats.totalCardioSessions}</p>
                </div>
              </div>
            </>
          )}

          {type === 'heatmap' && (
            <>
              <HeatmapGrid days={filteredStats.heatmapDays} />
              <div className="flex justify-between font-mono text-[9px] text-muted-foreground mt-1">
                <span>{filteredStats.rangeLabel} ago</span>
                <span className="text-primary">Today</span>
              </div>
              <div className="flex items-center gap-2 font-mono text-[9px] text-muted-foreground">
                <div className="w-3 h-3 rounded-sm bg-[#1f2937]" /> Rest day
                <div className="w-3 h-3 rounded-sm bg-primary ml-2" /> Workout
              </div>
            </>
          )}

          {type === 'muscles' && (
            <div className="space-y-3">
              {filteredStats.muscleGroupBreakdown.map(({ group, count }) => (
                <MuscleBar
                  key={group}
                  group={group}
                  count={count}
                  total={filteredStats.muscleGroupBreakdown.reduce((a, m) => a + m.count, 0)}
                />
              ))}
            </div>
          )}

          {type === 'prs' && (
            <div className="space-y-2 text-center">
              <p className="font-mono text-4xl font-bold text-primary">{stats.totalPRs}</p>
              <p className="font-mono text-xs text-muted-foreground uppercase tracking-wider">personal records set</p>
              {stats.mostTrainedExercise && (
                <p className="font-mono text-[10px] text-muted-foreground mt-2">
                  Most trained: <span className="text-foreground">{stats.mostTrainedExercise}</span>
                </p>
              )}
            </div>
          )}

          {type === 'sets' && (
            <div className="space-y-2 text-center">
              <p className="font-mono text-4xl font-bold text-primary">{filteredStats.totalSets.toLocaleString()}</p>
              <p className="font-mono text-xs text-muted-foreground uppercase tracking-wider">total sets completed</p>
              <p className="font-mono text-[10px] text-muted-foreground">
                avg {filteredStats.totalSessions > 0 ? Math.round(filteredStats.totalSets / filteredStats.totalSessions) : 0} sets per session
              </p>
            </div>
          )}

          {type === 'streak' && (
            <div className="space-y-3 text-center">
              <p className="font-mono text-5xl font-bold text-primary">{stats.currentStreak}</p>
              <p className="font-mono text-xs text-muted-foreground uppercase tracking-wider">day streak</p>
              <HeatmapGrid days={stats.heatmapDays.slice(-28)} />
              <p className="font-mono text-[9px] text-muted-foreground">Last 4 weeks</p>
            </div>
          )}

          {type === 'distance' && (
            <div className="space-y-2 text-center">
              <p className="font-mono text-4xl font-bold text-blue-400">{filteredStats.totalDistanceMiles}</p>
              <p className="font-mono text-xs text-muted-foreground uppercase tracking-wider">total miles</p>
              <BarChart
                data={filteredStats.cardioTrend.map((w) => ({ label: weekLabel(w.week), value: w.distanceMiles }))}
                color="#3b82f6"
              />
            </div>
          )}

          {type === 'pace' && stats.avgPaceMinPerMile && (
            <div className="space-y-2 text-center">
              <p className="font-mono text-4xl font-bold text-blue-400">{fmtPace(stats.avgPaceMinPerMile)}</p>
              <p className="font-mono text-xs text-muted-foreground uppercase tracking-wider">average pace</p>
              <p className="font-mono text-[10px] text-muted-foreground">across {filteredStats.totalCardioSessions} cardio sessions</p>
            </div>
          )}

          {type === 'hr' && stats.avgCardioHr && (
            <div className="space-y-2 text-center">
              <p className="font-mono text-4xl font-bold text-red-400">{stats.avgCardioHr}</p>
              <p className="font-mono text-xs text-muted-foreground uppercase tracking-wider">avg heart rate (bpm)</p>
              <p className="font-mono text-[10px] text-muted-foreground">across {filteredStats.totalCardioSessions} cardio sessions</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Availability Schedule ──────────────────────────────────────────────────
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const FULL_DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function AvailabilitySchedule() {
  const utils = trpc.useUtils();
  const { data: windows = [], isLoading } = trpc.availability.get.useQuery();

  // Local draft state — mirrors DB but editable
  const [draft, setDraft] = useState<Array<{ dayOfWeek: number; startTime: string; endTime: string }>>([]);
  const [editing, setEditing] = useState(false);

  // Sync draft from server when entering edit mode
  const startEditing = () => {
    setDraft(windows.map((w) => ({ dayOfWeek: w.dayOfWeek, startTime: w.startTime, endTime: w.endTime })));
    setEditing(true);
  };

  const save = trpc.availability.set.useMutation({
    onSuccess: () => {
      utils.availability.get.invalidate();
      toast.success('Schedule saved');
      setEditing(false);
    },
    onError: () => toast.error('Failed to save schedule'),
  });

  const toggleDay = (day: number) => {
    setDraft((prev) => {
      const exists = prev.find((w) => w.dayOfWeek === day);
      if (exists) return prev.filter((w) => w.dayOfWeek !== day);
      return [...prev, { dayOfWeek: day, startTime: '06:00', endTime: '08:00' }].sort((a, b) => a.dayOfWeek - b.dayOfWeek);
    });
  };

  const updateTime = (day: number, field: 'startTime' | 'endTime', value: string) => {
    setDraft((prev) => prev.map((w) => w.dayOfWeek === day ? { ...w, [field]: value } : w));
  };

  return (
    <Card className="border-border/60 bg-card">
      <CardHeader className="pb-2 pt-4 px-4">
        <CardTitle className="font-mono text-xs uppercase tracking-wider text-muted-foreground flex items-center justify-between">
          <span className="flex items-center gap-2"><Calendar className="h-3.5 w-3.5" /> My Training Schedule</span>
          {!editing && (
            <Button variant="ghost" size="sm" className="h-6 px-2 text-[10px] font-mono uppercase tracking-wider" onClick={startEditing}>
              <Pencil className="h-3 w-3 mr-1" /> Edit
            </Button>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="px-4 pb-4">
        {isLoading ? (
          <div className="h-8 bg-muted/30 rounded animate-pulse" />
        ) : !editing ? (
          // Read-only view
          windows.length === 0 ? (
            <div className="text-center py-4">
              <p className="font-mono text-xs text-muted-foreground uppercase tracking-wider">No schedule set</p>
              <p className="font-mono text-[10px] text-muted-foreground/60 mt-1">Set your available training days so your trainer can schedule workouts</p>
              <Button size="sm" className="mt-3 h-7 text-[10px] font-mono uppercase tracking-wider" onClick={startEditing}>
                <Plus className="h-3 w-3 mr-1" /> Set Schedule
              </Button>
            </div>
          ) : (
            <div className="space-y-1.5">
              {windows.map((w) => (
                <div key={w.id} className="flex items-center justify-between py-1.5 border-b border-border/30 last:border-0">
                  <span className="font-mono text-xs font-bold text-foreground uppercase tracking-wider w-12">{DAYS[w.dayOfWeek]}</span>
                  <span className="font-mono text-xs text-muted-foreground flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {w.startTime} – {w.endTime}
                  </span>
                </div>
              ))}
            </div>
          )
        ) : (
          // Edit mode
          <div className="space-y-3">
            <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Select available days and set your preferred time window</p>
            <div className="flex flex-wrap gap-1.5 mb-3">
              {DAYS.map((d, i) => {
                const active = draft.some((w) => w.dayOfWeek === i);
                return (
                  <button
                    key={i}
                    onClick={() => toggleDay(i)}
                    className={`font-mono text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-sm border transition-all ${
                      active
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-transparent text-muted-foreground border-border/60 hover:border-primary/50'
                    }`}
                  >
                    {d}
                  </button>
                );
              })}
            </div>
            {draft.length > 0 && (
              <div className="space-y-2">
                {draft.map((w) => (
                  <div key={w.dayOfWeek} className="flex items-center gap-2">
                    <span className="font-mono text-[10px] uppercase tracking-wider text-foreground w-10 shrink-0">{DAYS[w.dayOfWeek]}</span>
                    <input
                      type="time"
                      value={w.startTime}
                      onChange={(e) => updateTime(w.dayOfWeek, 'startTime', e.target.value)}
                      className="font-mono text-xs bg-muted/30 border border-border/60 rounded-sm px-2 py-1 text-foreground"
                    />
                    <span className="font-mono text-[10px] text-muted-foreground">–</span>
                    <input
                      type="time"
                      value={w.endTime}
                      onChange={(e) => updateTime(w.dayOfWeek, 'endTime', e.target.value)}
                      className="font-mono text-xs bg-muted/30 border border-border/60 rounded-sm px-2 py-1 text-foreground"
                    />
                    <button onClick={() => toggleDay(w.dayOfWeek)} className="text-muted-foreground hover:text-destructive transition-colors ml-auto">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex gap-2 pt-1">
              <Button size="sm" className="h-7 text-[10px] font-mono uppercase tracking-wider" disabled={save.isPending}
                onClick={() => save.mutate(draft)}>
                <Check className="h-3 w-3 mr-1" /> Save
              </Button>
              <Button variant="ghost" size="sm" className="h-7 text-[10px] font-mono uppercase tracking-wider"
                onClick={() => setEditing(false)}>
                Cancel
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Height/Weight settings form ──────────────────────────────────────────────
function ProfileSettings({ profile }: {
  profile: { heightFeet?: number | null; heightInches?: number | null; voiceLoggingEnabled?: boolean | null } | null | undefined
}) {
  const utils = trpc.useUtils();
  const [editing, setEditing] = useState(false);
  const [feet, setFeet] = useState(String(profile?.heightFeet ?? ''));
  const [inches, setInches] = useState(String(profile?.heightInches ?? ''));
  const [voiceEnabled, setVoiceEnabled] = useState<boolean>(profile?.voiceLoggingEnabled ?? false);

  const save = trpc.profile.save.useMutation({
    onSuccess: () => {
      utils.profile.get.invalidate();
      utils.profile.stats.invalidate();
      toast.success('Profile updated');
      setEditing(false);
    },
    onError: () => toast.error('Failed to save profile'),
  });

  const handleVoiceToggle = () => {
    const next = !voiceEnabled;
    setVoiceEnabled(next);
    save.mutate({ voiceLoggingEnabled: next });
  };

  const heightStr = profile?.heightFeet != null && profile?.heightInches != null
    ? `${profile.heightFeet}'${profile.heightInches}"`
    : 'Not set';

  return (
    <div className="space-y-4">
      {/* Height row */}
      {!editing ? (
        <div className="flex items-center justify-between">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Height</p>
            <p className="font-mono text-sm font-bold text-foreground">{heightStr}</p>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setEditing(true)} className="h-7 px-2">
            <Pencil className="h-3.5 w-3.5" />
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Height</p>
          <div className="flex items-center gap-2">
            <Input type="number" placeholder="ft" value={feet} onChange={(e) => setFeet(e.target.value)}
              className="w-16 font-mono text-sm h-8" min={4} max={8} />
            <span className="font-mono text-muted-foreground text-sm">ft</span>
            <Input type="number" placeholder="in" value={inches} onChange={(e) => setInches(e.target.value)}
              className="w-16 font-mono text-sm h-8" min={0} max={11} />
            <span className="font-mono text-muted-foreground text-sm">in</span>
            <Button size="sm" className="h-8 px-2" disabled={save.isPending}
              onClick={() => save.mutate({ heightFeet: feet ? Number(feet) : undefined, heightInches: inches ? Number(inches) : undefined })}>
              <Check className="h-3.5 w-3.5" />
            </Button>
            <Button variant="ghost" size="sm" className="h-8 px-2" onClick={() => setEditing(false)}>
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      )}

      {/* Voice logging toggle */}
      <div className="flex items-center justify-between border-t border-border/40 pt-3">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Voice Logging</p>
          <p className="font-mono text-[9px] text-muted-foreground/60 mt-0.5">Mic button in live session &amp; planner</p>
        </div>
        <button
          onClick={handleVoiceToggle}
          disabled={save.isPending}
          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none ${
            voiceEnabled ? 'bg-primary' : 'bg-muted'
          }`}
          aria-label="Toggle voice logging"
        >
          <span
            className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
              voiceEnabled ? 'translate-x-4' : 'translate-x-0.5'
            }`}
          />
        </button>
      </div>
    </div>
  );
}

// ─── Filtered stats type ──────────────────────────────────────────────────────
interface FilteredStats {
  totalSessions: number;
  totalVolumeLbs: number;
  totalSets: number;
  totalCardioSessions: number;
  totalDistanceMiles: number;
  currentWeight: number | null;
  startWeight: number | null;
  favoriteMuscleGroup: string | null;
  muscleGroupBreakdown: { group: string; count: number }[];
  last12Weeks: { week: string; count: number }[];
  volumeTrend: { week: string; volumeLbs: number }[];
  weightTrend: { date: string; weight: number; bodyFat: number | null }[];
  cardioTrend: { week: string; distanceMiles: number }[];
  heatmapDays: { date: string; count: number }[];
  rangeLabel: string;
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function UserProfile() {
  const { user } = useAuth();
  const { data: stats, isLoading } = trpc.profile.stats.useQuery();
  const { data: profile } = trpc.profile.get.useQuery();
  const { data: rawSessions = [] } = trpc.sessions.list.useQuery();
  const { data: rawWeightLogs = [] } = trpc.weight.list.useQuery();
  const { data: rawCardioSessions = [] } = trpc.cardio.list.useQuery();
  const [activeChart, setActiveChart] = useState<ChartType>(null);
  const [timeRange, setTimeRange] = useState<TimeRange>('all');

  // ── Compute filtered stats from raw data based on selected time range ──────
  const filteredStats = useMemo((): FilteredStats | null => {
    if (!stats) return null;

    const cutoff = getCutoff(timeRange);
    const rangeLabel = timeRange === '1m' ? '1 month' : timeRange === '3m' ? '3 months' : 'All time';

    // Filter completed sessions by date range
    const completedSessions = rawSessions.filter((s) => !!s.completedAt);
    const filteredSessions = cutoff
      ? completedSessions.filter((s) => s.date >= cutoff)
      : completedSessions;

    // Recompute strength aggregates
    let totalVolumeLbs = 0;
    let totalSets = 0;
    const categoryCount: Record<string, number> = {};
    const weeklyVolume: Record<string, number> = {};
    const weeklyVolumeLbs: Record<string, number> = {};

    for (const session of filteredSessions) {
      const pes = (session.plannedExercises as any[]) || [];
      for (const pe of pes) {
        const completedSets = ((pe.sets as any[]) || []).filter((s: any) => s.completed);
        totalSets += completedSets.length;
        for (const set of completedSets) {
          if (set.weight && set.reps) {
            totalVolumeLbs += set.weight * set.reps;
          }
        }
        // Category count for muscle breakdown (we don't have exercise data here,
        // so fall back to all-time breakdown when range is 'all', or use stats.muscleGroupBreakdown)
      }
      // Week key for frequency trend
      const d = new Date(session.date + 'T00:00:00');
      const year = d.getFullYear();
      const startOfYear = new Date(year, 0, 1);
      const weekNum = Math.ceil(((d.getTime() - startOfYear.getTime()) / 86400000 + startOfYear.getDay() + 1) / 7);
      const weekKey = `${year}-W${String(weekNum).padStart(2, '0')}`;
      weeklyVolume[weekKey] = (weeklyVolume[weekKey] ?? 0) + 1;
    }

    // Volume trend — build weekly buckets for the range
    // For all-time, derive weeks from actual session data rather than a fixed window
    const last12Weeks: { week: string; count: number }[] = [];
    const volumeTrend: { week: string; volumeLbs: number }[] = [];

    // Recompute volume per week from filtered sessions
    for (const session of filteredSessions) {
      const pes = (session.plannedExercises as any[]) || [];
      const d = new Date(session.date + 'T00:00:00');
      const year = d.getFullYear();
      const startOfYear = new Date(year, 0, 1);
      const weekNum = Math.ceil(((d.getTime() - startOfYear.getTime()) / 86400000 + startOfYear.getDay() + 1) / 7);
      const weekKey = `${year}-W${String(weekNum).padStart(2, '0')}`;
      for (const pe of pes) {
        const completedSets = ((pe.sets as any[]) || []).filter((s: any) => s.completed);
        for (const set of completedSets) {
          if (set.weight && set.reps) {
            weeklyVolumeLbs[weekKey] = (weeklyVolumeLbs[weekKey] ?? 0) + set.weight * set.reps;
          }
        }
      }
    }

    if (timeRange === 'all') {
      // For all-time: generate a contiguous week range from earliest session to today
      const allWeekKeys = new Set([
        ...Object.keys(weeklyVolume),
        ...Object.keys(weeklyVolumeLbs),
      ]);
      if (allWeekKeys.size > 0) {
        const sortedKeys = Array.from(allWeekKeys).sort();
        const firstKey = sortedKeys[0];
        // Build contiguous weeks from firstKey to current week
        const [firstYear, firstWStr] = firstKey.split('-W');
        let curDate = new Date();
        const endKey = (() => {
          const y = curDate.getFullYear();
          const soy = new Date(y, 0, 1);
          const wn = Math.ceil(((curDate.getTime() - soy.getTime()) / 86400000 + soy.getDay() + 1) / 7);
          return `${y}-W${String(wn).padStart(2, '0')}`;
        })();
        // Walk backwards from today to firstKey
        const weeks: string[] = [];
        let iter = new Date();
        while (true) {
          const y = iter.getFullYear();
          const soy = new Date(y, 0, 1);
          const wn = Math.ceil(((iter.getTime() - soy.getTime()) / 86400000 + soy.getDay() + 1) / 7);
          const wk = `${y}-W${String(wn).padStart(2, '0')}`;
          weeks.unshift(wk);
          if (wk <= firstKey) break;
          iter.setDate(iter.getDate() - 7);
          if (weeks.length > 260) break; // safety cap at 5 years
        }
        for (const wk of weeks) {
          last12Weeks.push({ week: wk, count: weeklyVolume[wk] ?? 0 });
          volumeTrend.push({ week: wk, volumeLbs: Math.round(weeklyVolumeLbs[wk] ?? 0) });
        }
      }
    } else {
      const weeksInRange = timeRange === '1m' ? 4 : 12;
      for (let i = weeksInRange - 1; i >= 0; i--) {
        const d = new Date();
        d.setDate(d.getDate() - i * 7);
        const year = d.getFullYear();
        const startOfYear = new Date(year, 0, 1);
        const weekNum = Math.ceil(((d.getTime() - startOfYear.getTime()) / 86400000 + startOfYear.getDay() + 1) / 7);
        const weekKey = `${year}-W${String(weekNum).padStart(2, '0')}`;
        last12Weeks.push({ week: weekKey, count: weeklyVolume[weekKey] ?? 0 });
        volumeTrend.push({ week: weekKey, volumeLbs: Math.round(weeklyVolumeLbs[weekKey] ?? 0) });
      }
    }

    // Weight trend — filter by date range
    const weightTrend = rawWeightLogs
      .filter((w) => !cutoff || w.date >= cutoff)
      .map((w) => ({ date: w.date, weight: w.weight, bodyFat: w.bodyFat ?? null }));

    const currentWeight = weightTrend.length > 0 ? weightTrend[weightTrend.length - 1].weight : null;
    const startWeight = weightTrend.length > 0 ? weightTrend[0].weight : null;

    // Cardio — filter by date range
    const filteredCardio = cutoff
      ? rawCardioSessions.filter((c) => c.date >= cutoff)
      : rawCardioSessions;

    const totalCardioSessions = filteredCardio.length;
    const totalDistanceMiles = Math.round(
      filteredCardio.reduce((a, c) => a + (c.distanceMiles ?? 0), 0) * 10
    ) / 10;

    // Cardio weekly trend
    const cardioWeeklyDist: Record<string, number> = {};
    for (const c of filteredCardio) {
      const d = new Date(c.date + 'T00:00:00');
      const y = d.getFullYear();
      const soy = new Date(y, 0, 1);
      const wn = Math.ceil(((d.getTime() - soy.getTime()) / 86400000 + soy.getDay() + 1) / 7);
      const wk = `${y}-W${String(wn).padStart(2, '0')}`;
      cardioWeeklyDist[wk] = (cardioWeeklyDist[wk] ?? 0) + (c.distanceMiles ?? 0);
    }
    const cardioTrend: { week: string; distanceMiles: number }[] = [];
    if (timeRange === 'all') {
      // All-time: use the same week keys derived from actual session data
      const cardioWeekKeys = Object.keys(cardioWeeklyDist).sort();
      if (cardioWeekKeys.length > 0) {
        const firstKey = cardioWeekKeys[0];
        const cardioWeeks: string[] = [];
        let iter = new Date();
        while (true) {
          const y = iter.getFullYear();
          const soy = new Date(y, 0, 1);
          const wn = Math.ceil(((iter.getTime() - soy.getTime()) / 86400000 + soy.getDay() + 1) / 7);
          const wk = `${y}-W${String(wn).padStart(2, '0')}`;
          cardioWeeks.unshift(wk);
          if (wk <= firstKey) break;
          iter.setDate(iter.getDate() - 7);
          if (cardioWeeks.length > 260) break;
        }
        for (const wk of cardioWeeks) {
          cardioTrend.push({ week: wk, distanceMiles: Math.round((cardioWeeklyDist[wk] ?? 0) * 10) / 10 });
        }
      }
    } else {
      const weeksInRange = timeRange === '1m' ? 4 : 12;
      for (let i = weeksInRange - 1; i >= 0; i--) {
        const d = new Date();
        d.setDate(d.getDate() - i * 7);
        const year = d.getFullYear();
        const startOfYear = new Date(year, 0, 1);
        const weekNum = Math.ceil(((d.getTime() - startOfYear.getTime()) / 86400000 + startOfYear.getDay() + 1) / 7);
        const weekKey = `${year}-W${String(weekNum).padStart(2, '0')}`;
        cardioTrend.push({ week: weekKey, distanceMiles: Math.round((cardioWeeklyDist[weekKey] ?? 0) * 10) / 10 });
      }
    }

    // Heatmap — build days for the range
    // For all-time, show all days from earliest session to today
    const heatmapDayCount = timeRange === '1m' ? 28 : timeRange === '3m' ? 84 : null; // null = all-time
    const sessionCountByDate = new Map<string, number>();
    for (const s of filteredSessions) {
      sessionCountByDate.set(s.date, (sessionCountByDate.get(s.date) ?? 0) + 1);
    }
    const heatmapDays: { date: string; count: number }[] = [];
    if (heatmapDayCount === null) {
      // All-time: generate days from earliest session date to today
      const earliestDate = filteredSessions.length > 0
        ? filteredSessions.reduce((min, s) => s.date < min ? s.date : min, filteredSessions[0].date)
        : null;
      if (earliestDate) {
        const start = new Date(earliestDate + 'T00:00:00');
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const iter = new Date(start);
        while (iter <= today) {
          const key = iter.toISOString().slice(0, 10);
          heatmapDays.push({ date: key, count: sessionCountByDate.get(key) ?? 0 });
          iter.setDate(iter.getDate() + 1);
        }
      }
    } else {
      for (let i = heatmapDayCount - 1; i >= 0; i--) {
        const d = new Date();
        d.setDate(d.getDate() - i);
        const key = d.toISOString().slice(0, 10);
        heatmapDays.push({ date: key, count: sessionCountByDate.get(key) ?? 0 });
      }
    }

    // Muscle group breakdown — use all-time from stats (exercise data not in raw sessions without join)
    // For range-filtered view, use stats.muscleGroupBreakdown as-is (all-time)
    const muscleGroupBreakdown = stats.muscleGroupBreakdown;
    const favoriteMuscleGroup = muscleGroupBreakdown[0]?.group ?? null;

    return {
      totalSessions: filteredSessions.length,
      totalVolumeLbs: Math.round(totalVolumeLbs),
      totalSets,
      totalCardioSessions,
      totalDistanceMiles,
      currentWeight,
      startWeight,
      favoriteMuscleGroup,
      muscleGroupBreakdown,
      last12Weeks,
      volumeTrend,
      weightTrend,
      cardioTrend,
      heatmapDays,
      rangeLabel,
    };
  }, [stats, rawSessions, rawWeightLogs, rawCardioSessions, timeRange]);

  if (isLoading) {
    return (
      <div className="space-y-4 animate-pulse pb-8">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="h-16 bg-muted/30 rounded-sm" />
        ))}
      </div>
    );
  }

  if (!stats || !filteredStats) return null;

  const heightStr = profile?.heightFeet != null && profile?.heightInches != null
    ? `${profile.heightFeet}'${profile.heightInches}"`
    : null;

  const volumeDisplay = filteredStats.totalVolumeLbs >= 1_000_000
    ? `${(filteredStats.totalVolumeLbs / 1_000_000).toFixed(1)}M`
    : filteredStats.totalVolumeLbs >= 1_000
    ? `${(filteredStats.totalVolumeLbs / 1_000).toFixed(1)}K`
    : filteredStats.totalVolumeLbs.toLocaleString();

  const weightDelta = filteredStats.currentWeight && filteredStats.startWeight
    ? filteredStats.currentWeight - filteredStats.startWeight
    : null;

  const rangeSub = timeRange === '1m' ? 'last 30 days' : timeRange === '3m' ? 'last 90 days' : 'all time';

  return (
    <div className="space-y-6 pb-8">
      {/* ── Header ── */}
      <div className="flex items-center gap-4 p-4 bg-card border border-border/60 rounded-sm">
        <div className="w-14 h-14 rounded-sm bg-primary/10 border border-primary/25 flex items-center justify-center shrink-0">
          <User className="h-7 w-7 text-primary" />
        </div>
        <div className="min-w-0">
          <h2 className="font-mono text-base font-bold uppercase tracking-wider text-foreground truncate">
            {user?.name ?? 'Athlete'}
          </h2>
          <p className="font-mono text-[11px] text-muted-foreground uppercase tracking-wider">
            {stats.totalSessions} sessions total
            {heightStr ? ` · ${heightStr}` : ''}
            {stats.currentWeight ? ` · ${stats.currentWeight} lbs` : ''}
          </p>
          {stats.favoriteMuscleGroup && (
            <p className="font-mono text-[10px] text-primary mt-0.5 uppercase tracking-wider">
              ★ {stats.favoriteMuscleGroup} specialist
            </p>
          )}
        </div>
      </div>

      {/* ── Time Range Toggle ── */}
      <div className="flex items-center justify-between">
        <p className="font-mono text-[9px] uppercase tracking-wider text-muted-foreground">Stats Range</p>
        <TimeRangeToggle value={timeRange} onChange={setTimeRange} />
      </div>

      {/* ── Strength stats ── */}
      <div>
        <p className="font-mono text-[9px] uppercase tracking-wider text-muted-foreground mb-2 px-0.5">Strength</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <StatCard icon={Dumbbell} label="Sessions" value={filteredStats.totalSessions}
            sub={rangeSub} accent onClick={() => setActiveChart('frequency')} />
          <StatCard icon={TrendingUp} label="Total Volume" value={`${volumeDisplay} lbs`}
            sub="weight × reps" onClick={() => setActiveChart('volume')} />
          <StatCard icon={Target} label="Total Sets" value={filteredStats.totalSets.toLocaleString()}
            sub={rangeSub} onClick={() => setActiveChart('sets')} />
          <StatCard icon={Trophy} label="PRs Set" value={stats.totalPRs}
            sub="personal records" onClick={() => setActiveChart('prs')} />
          <StatCard icon={Flame} label="Streak" value={`${stats.currentStreak}d`}
            sub="consecutive days" accent={stats.currentStreak >= 3}
            onClick={() => setActiveChart('streak')} />
          <StatCard icon={BarChart3} label="Muscle Focus" value={filteredStats.favoriteMuscleGroup ?? '—'}
            sub="most trained group" onClick={filteredStats.muscleGroupBreakdown.length > 0 ? () => setActiveChart('muscles') : undefined} />
        </div>
      </div>

      {/* ── Body stats ── */}
      {(filteredStats.currentWeight || filteredStats.weightTrend.length > 0) && (
        <div>
          <p className="font-mono text-[9px] uppercase tracking-wider text-muted-foreground mb-2 px-0.5">Body</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {filteredStats.currentWeight && (
              <StatCard icon={Scale} label="Current Weight" value={`${filteredStats.currentWeight} lbs`}
                sub={weightDelta != null ? `${weightDelta > 0 ? '+' : ''}${weightDelta.toFixed(1)} lbs from start` : 'latest log'}
                onClick={filteredStats.weightTrend.length >= 2 ? () => setActiveChart('weight') : undefined} />
            )}
            {filteredStats.weightTrend.length > 0 && (
              <div className="col-span-2 sm:col-span-2 p-3 bg-card border border-border/60 rounded-sm">
                <p className="font-mono text-[9px] uppercase tracking-wider text-muted-foreground mb-2">
                  Weight — {timeRange === '1m' ? '30 Days' : timeRange === '3m' ? '90 Days' : 'All Time'}
                </p>
                <LineChart
                  data={filteredStats.weightTrend.map((w) => w.weight)}
                  labels={filteredStats.weightTrend.map((w) => w.date)}
                  color="#22c55e"
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Cardio stats ── */}
      {filteredStats.totalCardioSessions > 0 && (
        <div>
          <p className="font-mono text-[9px] uppercase tracking-wider text-muted-foreground mb-2 px-0.5">Cardio</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatCard icon={Activity} label="Cardio Sessions" value={filteredStats.totalCardioSessions}
              sub={rangeSub} onClick={() => setActiveChart('cardio')} />
            {filteredStats.totalDistanceMiles > 0 && (
              <StatCard icon={MapPin} label="Total Distance" value={`${filteredStats.totalDistanceMiles} mi`}
                sub={rangeSub} onClick={() => setActiveChart('distance')} />
            )}
            {stats.avgPaceMinPerMile && (
              <StatCard icon={Timer} label="Avg Pace" value={fmtPace(stats.avgPaceMinPerMile)}
                sub="per mile" onClick={() => setActiveChart('pace')} />
            )}
            {stats.avgCardioHr && (
              <StatCard icon={Heart} label="Avg HR" value={`${stats.avgCardioHr} bpm`}
                sub="during cardio" onClick={() => setActiveChart('hr')} />
            )}
          </div>
        </div>
      )}

      {/* ── Workout heatmap ── */}
      <div>
        <p className="font-mono text-[9px] uppercase tracking-wider text-muted-foreground mb-2 px-0.5">Activity</p>
        <button
          onClick={() => setActiveChart('heatmap')}
          className="w-full p-3 bg-card border border-border/60 rounded-sm hover:border-primary/40 transition-colors text-left"
        >
          <p className="font-mono text-[9px] uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
            <Zap className="h-3 w-3" /> Workout Heatmap — {timeRange === '1m' ? '4 Weeks' : '12 Weeks'}
          </p>
          <HeatmapGrid days={filteredStats.heatmapDays} />
          <p className="font-mono text-[8px] text-primary/60 mt-2 uppercase tracking-wider">tap to expand →</p>
        </button>
      </div>

      {/* ── My Schedule ── */}
      <AvailabilitySchedule />

      {/* ── Settings ── */}
      <Card className="border-border/60 bg-card">
        <CardHeader className="pb-2 pt-4 px-4">
          <CardTitle className="font-mono text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-2">
            <User className="h-3.5 w-3.5" /> Settings
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-4">
          <ProfileSettings profile={profile} />
        </CardContent>
      </Card>

      {/* ── Empty state ── */}
      {filteredStats.totalSessions === 0 && (
        <div className="py-12 text-center space-y-2">
          <Dumbbell className="h-10 w-10 text-muted-foreground/40 mx-auto" />
          <p className="font-mono text-sm text-muted-foreground uppercase tracking-wider">
            {timeRange === 'all' ? 'No completed sessions yet' : `No sessions in the last ${timeRange === '1m' ? '30' : '90'} days`}
          </p>
          <p className="font-mono text-xs text-muted-foreground/60">
            {timeRange === 'all' ? 'Complete your first workout to see stats here' : 'Try selecting a wider time range'}
          </p>
        </div>
      )}

      {/* ── Drill-down dialog ── */}
      {activeChart && (
        <DrillDownDialog
          type={activeChart}
          stats={stats}
          filteredStats={filteredStats}
          onClose={() => setActiveChart(null)}
        />
      )}
    </div>
  );
}
